/**
 *
 * @Copyright 2024 UNLOCKIT DECENTRALIZATION, LDA
 * Development by VOID Software, SA
 *
 */

import {
    FormEventHandler,
    FunctionComponent, Suspense, lazy, useEffect, useRef, useState,
} from 'react';
import { VariableSizeList } from 'react-window';
import { ErrorResponse } from '../../../../types/errors';
import { QesSignatureContext, withQesSignatureContext } from '../../../controllers/esignatures/QesSignatureContext';
import { TranslationContext, withTranslationContext } from '../../../controllers/TranslationContext';
import Button from '../../../elements/Button';
import { ButtonVariant } from '../../../../types/general';
import { ReactComponent as ArrowRightIcon } from '../../../../assets/images/arrow-right.svg';
import { EsignaturesLayout } from '../../../elements/esignatures/layouts/EsignaturesLayout';
import ElectronicIDLogo from '../../../../assets/images/logo-electronic-id.png';
import { LoadingScreen } from '../../LoadingScreen';
import { Checkbox } from '../../../elements/Checkbox';

const PdfViewerWrapper = lazy(() => import('../../../elements/pdfViewer/PdfViewerWrapper'));

type OwnProps = TranslationContext & QesSignatureContext;

const QesSignaturePrivacyPolicyScreenComponent: FunctionComponent<OwnProps> = (props) => {
    const {
        t, acceptQesVideoPrivacyPolicy, navigateToContract, getQesVideoPrivacyPolicy, navigateToStartVideoValidation: navigateToReadyToRoll,
    } = props;

    const [hasTermsError, setHasTermsError] = useState<boolean>(false);
    const [error, setError] = useState<ErrorResponse>();
    const [privacyPolicyPdfUrl, setPrivacyPolicyPdfUrl] = useState<string | null>(null);

    const pdfPagesListRef = useRef<VariableSizeList>();

    useEffect(() => {
        if (error) throw error;
    }, [error]);

    useEffect(() => {
        getQesVideoPrivacyPolicy().then((res) => {
            if (res[1]) {
                setError(res[1]);
                return;
            }

            setPrivacyPolicyPdfUrl(URL.createObjectURL(res[0]));
        });
    }, []);

    /**
    * JSX for the "Go Back" button used in the existing bottom bar of the layout.
    *
    */
    const goBackBtn = (
        <Button
            id="actionBackToViewSigningContract"
            variant={ButtonVariant.Curved}
            extraClasses="secondary"
            onClick={navigateToContract}
            testId="actionBackToViewSigningContract"
        >
            <span className="btn-content">
                {t('qesSignaturePrivacyPolicy.actionGoBack')}
            </span>
        </Button>
    );

    /**
    * JSX for the "Next" button used in the existing bottom bar of the layout.
    *
    */
    const nextBtn = (
        <Button
            id="actionNext"
            variant={ButtonVariant.Curved}
            extraClasses="primary"
            testId="actionNext"
            isSubmit
        >
            <span className="btn-content">
                {t('qesSignaturePrivacyPolicy.actionNext')}
                <ArrowRightIcon />
            </span>
        </Button>
    );

    const handleNextAction: FormEventHandler = async (e) => {
        e.preventDefault();

        const formData = new FormData(e.target as HTMLFormElement);
        const checkboxValue = formData.get('accept-terms-n-condition');

        if (checkboxValue === null) {
            setHasTermsError(true);

            return;
        }

        const res = await acceptQesVideoPrivacyPolicy();
        if (res[1]) {
            setError(res[1]);
            return;
        }

        navigateToReadyToRoll();
    };

    return (
        <form onSubmit={handleNextAction} className="qes-signature-documents u-bg bg-secondary">
            <EsignaturesLayout primaryBtn={nextBtn} secondaryBtn={goBackBtn}>
                <div className="qes-signature-layout">
                    <div className="qes-signature-banner">
                        <img src={ElectronicIDLogo} alt="Electronic IDentification logo" height={25} width="auto" />
                    </div>
                    <section className="qes-signature-body">
                        <h1>{t('qesSignaturePrivacyPolicy.title')}</h1>
                        {privacyPolicyPdfUrl && (
                            <Suspense fallback={<LoadingScreen />}>
                                <PdfViewerWrapper
                                    fileUrl={privacyPolicyPdfUrl}
                                    pdfPagesListRef={pdfPagesListRef}
                                />
                            </Suspense>
                        )}
                    </section>
                    <footer className="qes-signature-footer">
                        <Checkbox
                            id="accept-terms-n-condition"
                            label={t('qesSignaturePrivacyPolicy.checkboxLabel')}
                            name="accept-terms-n-condition"
                            hasError={hasTermsError}
                            onChange={() => setHasTermsError(false)}
                        />
                    </footer>
                </div>
            </EsignaturesLayout>
        </form>
    );
};

export const QesSignaturePrivacyPolicyScreen = withQesSignatureContext(withTranslationContext(QesSignaturePrivacyPolicyScreenComponent));
